import { Dispatch, SetStateAction } from 'react';
import { DropResult } from 'react-beautiful-dnd';
import { RoutineForm } from '../validators';

interface UseRoutineDragEndOptions {
  form: RoutineForm;
  setForm: Dispatch<SetStateAction<RoutineForm>>;
  expandedExerciseIds: Record<string, boolean>;
  setExpandedExerciseIds: Dispatch<SetStateAction<Record<string, boolean>>>;
}

export const useRoutineDragEnd = ({ form, setForm, expandedExerciseIds, setExpandedExerciseIds }: UseRoutineDragEndOptions) => {
  // Función para manejar el arrastre de días
  function handleDayDragEnd(result: DropResult) {
    const { source, destination } = result;

    if (!destination || source.index === destination.index) return;

    // Obtener el estado expandido antes del cambio
    const currentExpandedState = { ...expandedExerciseIds };

    try {
      // Crear una copia profunda de exerciseDays
      const updatedDays = form.exerciseDays.map((day) => ({
        ...day,
        exercises: [...day.exercises],
      }));

      // Obtener el día que estamos moviendo
      const dayToMove = {
        ...updatedDays[source.index],
        exercises: [...updatedDays[source.index].exercises],
      };

      // Quitar el elemento de su posición original
      updatedDays.splice(source.index, 1);

      // Insertar el elemento en su nueva posición
      updatedDays.splice(destination.index, 0, dayToMove);

      // Renumerar TODOS los días de forma explícita
      const renumberedDays = updatedDays.map((day, index) => {
        const newDayNumber = index + 1;

        let updatedName = day.name;
        if (!updatedName || /^Día \d+$/.test(updatedName)) {
          updatedName = `Día ${newDayNumber}`;
        }

        return {
          ...day,
          dayNumber: newDayNumber,
          id: `day-${newDayNumber}`,
          name: updatedName,
        };
      });

      // Actualizar el estado del formulario
      setForm((prev) => ({
        ...prev,
        exerciseDays: renumberedDays,
      }));

      // Crear un mapa actualizado para los IDs de ejercicios expandidos
      const updatedExpandedIds: Record<string, boolean> = {};

      Object.keys(currentExpandedState).forEach((key) => {
        const [oldDayIndex, exerciseIndex] = key.split('-').map(Number);

        if (oldDayIndex === source.index) {
          // El ejercicio se mueve al nuevo índice del día
          updatedExpandedIds[`${destination.index}-${exerciseIndex}`] = true;
        } else if (source.index < destination.index && oldDayIndex > source.index && oldDayIndex <= destination.index) {
          // El día se desplaza una posición hacia arriba
          updatedExpandedIds[`${oldDayIndex - 1}-${exerciseIndex}`] = true;
        } else if (source.index > destination.index && oldDayIndex >= destination.index && oldDayIndex < source.index) {
          // El día se desplaza una posición hacia abajo
          updatedExpandedIds[`${oldDayIndex + 1}-${exerciseIndex}`] = true;
        } else {
          // No se ve afectado por el movimiento
          updatedExpandedIds[key] = true;
        }
      });

      // Restaurar los acordeones expandidos con los nuevos IDs
      setTimeout(() => {
        setExpandedExerciseIds(updatedExpandedIds);
      }, 10);
    } catch (error) {
      console.error('Error al mover días:', error);
    }
  }

  // Función para manejar el fin de un arrastre (tanto para días como para ejercicios)
  function handleDragEnd(result: DropResult) {
    if (!result.destination) return;

    if (result.type === 'days') {
      handleDayDragEnd(result);
    } else if (result.type === 'exercises') {
      const sourceDroppableId = result.source.droppableId;
      const destDroppableId = result.destination.droppableId;

      const sourceDayIndex = parseInt(sourceDroppableId.split('-')[1], 10);
      const destDayIndex = parseInt(destDroppableId.split('-')[1], 10);

      const sourceIndex = result.source.index;
      const destIndex = result.destination.index;

      if (sourceDayIndex === destDayIndex && sourceIndex === destIndex) return;

      // Obtener el estado expandido antes del cambio
      const expandedState = { ...expandedExerciseIds };

      // Hacer una copia profunda de exerciseDays
      const newExerciseDays = [...form.exerciseDays];
      const newSourceDay = { ...newExerciseDays[sourceDayIndex] };
      const newDestDay = sourceDayIndex === destDayIndex ? newSourceDay : { ...newExerciseDays[destDayIndex] };

      newSourceDay.exercises = [...newSourceDay.exercises];
      if (sourceDayIndex !== destDayIndex) {
        newDestDay.exercises = [...newDestDay.exercises];
      }

      // Mover el ejercicio
      const [movedExercise] = newSourceDay.exercises.splice(sourceIndex, 1);
      newDestDay.exercises.splice(destIndex, 0, movedExercise);

      // Actualizar los arrays
      newExerciseDays[sourceDayIndex] = newSourceDay;
      if (sourceDayIndex !== destDayIndex) {
        newExerciseDays[destDayIndex] = newDestDay;
      }

      setForm((prev) => ({
        ...prev,
        exerciseDays: newExerciseDays,
      }));

      // Restaurar el estado expandido
      setTimeout(() => {
        setExpandedExerciseIds(expandedState);
      }, 0);
    }
  }

  return handleDragEnd;
};
